// frontend/src/pages/SperrlisteView.tsx
import { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  Table, TableHead, TableBody, TableRow, TableCell, TablePagination,
  Chip, Button,
  InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Alert
} from '@mui/material';
import { Search, Add, Upload } from '@mui/icons-material';
import { ProhibitInset } from '@phosphor-icons/react';
import SubPageHeader from '../components/SubPageHeader';
import { getApiUrl, getIngestUrl, authFetch } from '../config';
import SperrlisteFormDialog from './components/SperrlisteFormDialog';
import SperrlisteImportDialog from './components/SperrlisteImportDialog';
import ExportJobsManager from './components/ExportJobsManager';

interface SperrlisteRecord {
  id: number;
  kategorie: string;
  vnm: string;
  nnm: string;
  str: string;
  hnr: string;
  plz: string;
  ort: string;
  tns: string;
  hns: string;
  fns: string;
  ema: string;
  lkn: string;
  active: boolean;
  inf3_short: string;
  created_at: string;
}

interface Stats {
  total: number;
  active: number;
  by_kategorie: { kategorie: string; count: number; active_count: number }[];
}

const KATEGORIE_COLORS: Record<string, string> = {
  'BOESE-BUBEN': '#dc2626',
  'BLACKLIST-BOESCHE-SPEZIAL': '#ea580c',
  'BLACKLIST-BOESCHE-INTERN': '#d97706',
};

export default function SperrlisteView() {
  const [records, setRecords] = useState<SperrlisteRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [search, setSearch] = useState('');
  const [kategorie, setKategorie] = useState('');
  const [kategorien, setKategorien] = useState<string[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);

  // Dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [formRecord, setFormRecord] = useState<SperrlisteRecord | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [querulantMonat, setQuerulantMonat] = useState('');
  const [querulantImporting, setQuerulantImporting] = useState(false);
  const [querulantMonate, setQuerulantMonate] = useState<{ telefon_monat: string; cnt: number }[]>([]);
  const [querulantResult, setQuerulantResult] = useState<string>('');

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
      });
      if (search) params.set('search', search);
      if (kategorie) params.set('kategorie', kategorie);

      const res = await authFetch(getApiUrl(`/sperrliste?${params}`));
      const data = await res.json();
      if (data.success) {
        setRecords(data.records);
        setTotal(data.pagination.total);
      }
    } catch (err) {
      console.error('Fehler beim Laden:', err);
    }
    setLoading(false);
  }, [page, rowsPerPage, search, kategorie]);

  const loadStats = useCallback(async () => {
    try {
      const res = await authFetch(getApiUrl('/sperrliste/stats'));
      const data = await res.json();
      if (data.success) setStats(data);
    } catch (err) {
      console.error('Fehler beim Laden der Statistik:', err);
    }
  }, []);

  const loadKategorien = useCallback(async () => {
    try {
      const res = await authFetch(getApiUrl('/sperrliste/kategorien'));
      const data = await res.json();
      if (data.success) setKategorien(data.kategorien);
    } catch (err) {
      console.error('Fehler beim Laden der Kategorien:', err);
    }
  }, []);

  const loadQuerulantMonate = useCallback(async () => {
    try {
      const res = await authFetch(getIngestUrl('/datenpflege/querulanten-monate'));
      const data = await res.json();
      if (data.success) setQuerulantMonate(data.monate);
    } catch (err) {
      console.error('Fehler beim Laden der Monate:', err);
    }
  }, []);

  useEffect(() => { loadRecords(); }, [loadRecords]);
  useEffect(() => {
    loadStats(); loadKategorien(); loadQuerulantMonate();
  }, [loadStats, loadKategorien, loadQuerulantMonate]);

  const handleFormSave = () => {
    setFormOpen(false);
    setFormRecord(null);
    loadRecords();
    loadStats();
    loadKategorien();
  };

  const handleImportDone = () => {
    setImportOpen(false);
    loadRecords();
    loadStats();
    loadKategorien();
  };

  const handleQuerulantImport = async () => {
    setQuerulantImporting(true);
    setQuerulantResult('');
    try {
      const res = await authFetch(getIngestUrl('/datenpflege/import-querulanten'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monat: querulantMonat || null }),
      });
      const data = await res.json();
      if (data.success) {
        const skipped = data.skipped || 0;
        const parts = [`${data.count.toLocaleString()} Querulanten importiert`];
        if (skipped > 0) parts.push(`${skipped.toLocaleString()} Duplikate übersprungen`);
        setQuerulantResult(`✓ ${parts.join(', ')}`);
        loadRecords();
        loadStats();
        loadKategorien();
        loadQuerulantMonate();
      } else {
        setQuerulantResult(`✗ Fehler: ${data.error || 'unbekannt'}`);
      }
    } catch (err) {
      setQuerulantResult(`✗ ${(err as Error).message}`);
    }
    setQuerulantImporting(false);
  };

  // Dialfire-Sync (Simulation)
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncResult, setSyncResult] = useState<any | null>(null);

  const runDialfireSync = async () => {
    setSyncRunning(true);
    setSyncResult(null);
    try {
      const res = await authFetch(getApiUrl('/sperrliste/dialfire-sync?dry_run=true'), { method: 'POST' });
      const data = await res.json();
      setSyncResult(data);
      setSyncOpen(true);
    } catch (err) {
      setSyncResult({ success: false, error: (err as Error).message });
      setSyncOpen(true);
    }
    setSyncRunning(false);
  };

  const phaseLabel = (phase: string) => ({
    count_initial: '1. Initial-Bestand',
    delete: '2. Löschen',
    verify_empty: '3. Leerung verifizieren',
    upload: '4. Upload',
    verify_final: '5. Final-Bestand verifizieren',
  } as Record<string, string>)[phase] || phase;

  const getKategorieColor = (kat: string) => {
    return KATEGORIE_COLORS[kat] || '#6b7280';
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <SubPageHeader title="Zentrale Sperrlisten Verwaltung" icon={<ProhibitInset size={28} weight="fill" />} />

      {/* Export-Jobs-Sektion */}
      <Box sx={{ mb: 3 }}>
        <ExportJobsManager
          toolbarExtra={
            <Button size="small" variant="contained" color="primary" onClick={runDialfireSync} disabled={syncRunning}>
              {syncRunning ? 'Simuliere…' : 'Dialfire-Sync (Simulation)'}
            </Button>
          }
        />
      </Box>

      {/* Querulanten Import */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#991b1b' }}>
          Querulanten aus Dialfire:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Monat</InputLabel>
          <Select
            value={querulantMonat}
            label="Monat"
            onChange={(e) => setQuerulantMonat(e.target.value)}
          >
            <MenuItem value="">Alle Monate</MenuItem>
            {querulantMonate.map(m => (
              <MenuItem key={m.telefon_monat} value={m.telefon_monat}>
                {m.telefon_monat} ({m.cnt.toLocaleString()})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button size="small" variant="contained" color="error" onClick={handleQuerulantImport} disabled={querulantImporting}>
          {querulantImporting ? 'Importiere…' : 'Importieren'}
        </Button>
        {querulantResult && (
          <Typography variant="body2" sx={{
            color: querulantResult.startsWith('✓') ? 'success.main' : 'error.main',
            fontWeight: 500
          }}>
            {querulantResult}
          </Typography>
        )}
      </Paper>

      {/* Search + Filter Bar */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Name, PLZ, Telefon, E-Mail..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
          }}
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Kategorie</InputLabel>
          <Select
            value={kategorie}
            label="Kategorie"
            onChange={(e) => { setKategorie(e.target.value); setPage(0); }}
          >
            <MenuItem value="">Alle</MenuItem>
            {kategorien.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
          </Select>
        </FormControl>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" startIcon={<Upload />} onClick={() => setImportOpen(true)}>
          CSV Import
        </Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setFormRecord(null); setFormOpen(true); }}>
          Neu
        </Button>
      </Paper>

      {/* Table */}
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Kategorie</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Adresse</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>E-Mail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map(r => (
              <TableRow key={r.id} hover sx={{ opacity: r.active ? 1 : 0.5 }}>
                <TableCell>
                  <Chip
                    label={r.kategorie}
                    size="small"
                    sx={{
                      backgroundColor: getKategorieColor(r.kategorie) + '18',
                      color: getKategorieColor(r.kategorie),
                      fontWeight: 500,
                      fontSize: '0.7rem'
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{r.vnm} {r.nnm}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{r.plz} {r.ort}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {r.str}{r.hnr && !r.str.endsWith(r.hnr) ? ` ${r.hnr}` : ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  {[{ v: r.tns, l: 'Tel' }, { v: r.hns, l: 'Handy' }, { v: r.fns, l: 'Fax' }]
                    .filter(x => x.v)
                    .map((x, i) => (
                      <Typography key={i} variant="body2">
                        {x.v}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">{x.l}</Typography>
                      </Typography>
                    ))}
                  {!(r.tns || r.hns || r.fns) && <Typography variant="body2">—</Typography>}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{r.ema || '—'}</Typography>
                </TableCell>
              </TableRow>
            ))}
            {records.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {search || kategorie ? 'Keine Treffer' : 'Keine Einträge vorhanden'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Pro Seite:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count.toLocaleString()}`}
        />
      </Paper>

      {/* Dialogs */}
      <SperrlisteFormDialog
        open={formOpen}
        record={formRecord}
        onClose={() => { setFormOpen(false); setFormRecord(null); }}
        onSave={handleFormSave}
      />
      <SperrlisteImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={handleImportDone}
      />

      {/* Dialfire-Sync-Dialog */}
      <Dialog open={syncOpen} onClose={() => setSyncOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Dialfire-Sync — Simulation</DialogTitle>
        <DialogContent>
          {syncResult?.success ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                {syncResult.message}
              </Alert>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>{syncResult.summary.campaigns}</strong> Kampagnen ·{' '}
                <strong>{syncResult.summary.phone_count.toLocaleString('de-DE')}</strong> Nummern ·{' '}
                <strong>{syncResult.summary.total_api_calls}</strong> geplante API-Calls
              </Typography>
              {(['count_initial', 'delete', 'verify_empty', 'upload', 'verify_final'] as const).map(phase => (
                <Box key={phase} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'primary.main' }}>
                    {phaseLabel(phase)}
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      {syncResult.steps.filter((s: any) => s.phase === phase).map((s: any, i: number) => (
                        <TableRow key={i}>
                          <TableCell sx={{ width: 120 }}>{s.campaign}</TableCell>
                          <TableCell>{s.description}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
                            {s.detail}
                          </TableCell>
                          <TableCell sx={{ width: 80 }}>
                            <Chip size="small" label={s.status} variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ))}
            </>
          ) : (
            <Alert severity="error">
              {syncResult?.error || 'Unbekannter Fehler'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
